import apiClient from "./client";
import type {
  BalanceSource,
  CalcCategory,
  CreateDebtPayload,
  Debt,
  DebtOverview,
  DebtPayoffInfo,
  DebtPlan,
  DebtStatus,
  DebtSummary,
  DebtType,
  ExtraPaymentSource,
  PaymentFrequency,
  PayoffStrategy,
  StrategyBrief,
  StrategyRecommendation,
  StrategyRecommendationBranch,
  UpdateDebtPayload,
  UpdatePlanPayload,
} from "@fomofi/shared";

/* ─────────────────────── API response types (snake_case) ───────────────────────
 * Mirrors backend/apps/debts/serializers.py + services/summary.py.
 * Monetary values arrive as Decimal strings; the adapter parses them to numbers. */

export interface ApiDebt {
  id: number;
  name: string;
  debt_type: DebtType;
  calc_category: CalcCategory;
  status: DebtStatus;
  balance_source: BalanceSource;
  current_balance: string;
  apr: string;
  minimum_payment: string;
  escrow_amount: string;
  due_date: number | null;
  next_due_date: string | null;
  payment_frequency: PaymentFrequency;
  account_id: number | null;
  last_four: string;
  is_active: boolean;
  extra_payments_allowed: boolean;
  lender: string;
  notes: string;
  original_amount: string | null;
  term_months: number | null;
  created_at: string;
  updated_at: string;
}

export interface ApiStrategyBrief {
  payoff_date: string | null;
  months: number | null;
  total_interest: string | null;
  never_pays_off: boolean;
}

export interface ApiStrategyRecommendationBranch {
  first_debt_name: string | null;
  first_debt_months: number | null;
  first_debt_payoff_date: string | null;
  total_interest: string;
  months_to_debt_free: number | null;
  never_pays_off: boolean;
}

export interface ApiStrategyRecommendation {
  recommended: PayoffStrategy;
  reason: string;
  /** See `StrategyRecommendation.noPreference`. Optional for a client deployed
   *  ahead of the API, which then behaves exactly as before. */
  no_preference?: boolean;
  snowball: ApiStrategyRecommendationBranch;
  avalanche: ApiStrategyRecommendationBranch;
}

export interface ApiDebtSummary {
  total_debt: string;
  account_count: number;
  payoff_date_minimums: string | null;
  payoff_date_strategy: string | null;
  months_saved: number | null;
  time_saved_label: string;
  interest_saved: string | null;
  interest_total_minimums: string | null;
  interest_total_strategy: string | null;
  minimums_never_pay_off?: boolean;
  strategies: Record<PayoffStrategy, ApiStrategyBrief>;
  recommendation: ApiStrategyRecommendation;
  never_pays_off: boolean;
  due_date_warning: boolean;
  /** FP-040 lockout. Optional for a client deployed ahead of the API. */
  projection_excluded_count?: number;
  projection_unavailable_message?: string | null;
}

export interface ApiDebtPlan {
  strategy: PayoffStrategy;
  extra_payment: string;
  /** See shared/types/debt.ts for what each value means and why it was renamed twice. */
  extra_payment_source: ExtraPaymentSource;
  pay_frequency: string;
  pay_frequency_label: string;
  extra_payment_monthly?: string;
  extra_payment_applies?: boolean;
}

export interface ApiPerDebtPayoff {
  order: number;
  payoff_date: string | null;
  duration_label: string;
  never_pays_off: boolean;
  payoff_date_minimums: string | null;
  projected_balance_at_focus: string | null;
  total_payment: string | null;
  /** FP-149: the engine's monthly minimum. Optional: an older API has none. */
  minimum_monthly?: string | null;
  anchor: "due_date" | "today_fallback";
  projection_unavailable?: boolean;
  /** FP-087: an active debt with nothing owed, left out of the plan. */
  nothing_owed?: boolean;
}

export interface ApiBalanceOverTimePoint {
  year: number;
  minimums: string;
  strategy: string;
}

/** One row of the payment ledger behind the "Savings & Debt" actual (#487).
 *  Derived server-side from the user's own transactions and debts, newest first
 *  and capped; read-only, so there is nothing here to write back. */
export interface ApiDebtPayment {
  id: number;
  debt_id: number;
  debt_name: string;
  date: string;
  amount: string;
  principal_amount: string;
  interest_amount: string;
  escrow_amount: string;
  apr: string;
  balance: string;
  source: string;
}

export interface ApiDebtOverview {
  debts: ApiDebt[];
  paid_off_debts: ApiDebt[];
  plan: ApiDebtPlan;
  summary: ApiDebtSummary;
  per_debt_payoff: Record<string, ApiPerDebtPayoff>;
  balance_over_time: ApiBalanceOverTimePoint[];
  /** Typed, not yet rendered. This type declares itself a mirror of the payload,
   *  and until #1046 it was silently short of this section, so nothing could tell
   *  a reader whether the backend sent it. No page reads it today: it is the
   *  provenance for a figure the Insights page already prints, and a debt payment
   *  history is its own screen. */
  recent_payments: ApiDebtPayment[];
}

/* ───────────────────────────────── requests ───────────────────────────────── */

export async function getDebtOverview(): Promise<ApiDebtOverview> {
  const { data } = await apiClient.get<ApiDebtOverview>("/debts/");
  return data;
}

/** Map the UI's create/update payload onto the backend's write serializer. */
function toWriteBody(p: CreateDebtPayload | UpdateDebtPayload): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if ("candidateKey" in p && p.candidateKey) body.candidate_key = p.candidateKey;
  if (p.name !== undefined) body.name = p.name;
  if (p.type !== undefined) body.debt_type = p.type;
  if (p.balanceSource !== undefined) body.balance_source = p.balanceSource;
  if (p.balance !== undefined) body.balance = p.balance;
  if (p.apr !== undefined) body.apr = p.apr;
  if (p.minimumPayment !== undefined) body.minimum_payment = p.minimumPayment;
  if (p.escrowAmount !== undefined) body.escrow_amount = p.escrowAmount;
  if (p.dueDate !== undefined) body.due_date = p.dueDate;
  if (p.nextDueDate !== undefined) body.next_due_date = p.nextDueDate;
  if (p.paymentFrequency !== undefined) body.payment_frequency = p.paymentFrequency;
  if (p.accountId !== undefined) body.account = p.accountId;
  if (p.status !== undefined) body.status = p.status;
  if (p.extraPaymentsAllowed !== undefined) body.extra_payments_allowed = p.extraPaymentsAllowed;
  if (p.lender !== undefined) body.lender = p.lender;
  if (p.notes !== undefined) body.notes = p.notes;
  if (p.originalAmount !== undefined) body.original_amount = p.originalAmount;
  if (p.termMonths !== undefined) body.term_months = p.termMonths;
  return body;
}

export async function createDebt(payload: CreateDebtPayload): Promise<ApiDebt> {
  const { data } = await apiClient.post<ApiDebt>("/debts/list/", toWriteBody(payload));
  return data;
}

export async function updateDebt(id: number, payload: UpdateDebtPayload): Promise<ApiDebt> {
  const { data } = await apiClient.patch<ApiDebt>(`/debts/${id}/`, toWriteBody(payload));
  return data;
}

export async function deleteDebt(id: number): Promise<void> {
  await apiClient.delete(`/debts/${id}/`);
}

export async function updateDebtPlan(payload: UpdatePlanPayload): Promise<ApiDebtPlan> {
  const body: Record<string, unknown> = {};
  if (payload.strategy !== undefined) body.strategy = payload.strategy;
  // `null` is a meaningful value here (clear the override), so the presence check
  // has to be `!== undefined` rather than a truthiness test.
  if (payload.extraPayment !== undefined) body.extra_payment = payload.extraPayment;
  if (payload.extraPaymentSource !== undefined) {
    body.extra_payment_source = payload.extraPaymentSource;
  }
  const { data } = await apiClient.patch<ApiDebtPlan>("/debts/plan/", body);
  return data;
}

/* ───────────────────────────────── adapter ───────────────────────────────── */

const num = (s: string | null | undefined): number => (s == null ? 0 : parseFloat(s));
/** A figure the server can withhold (contract batch 8a): null stays null, never $0. */
const nullableNum = (s: string | null | undefined): number | null =>
  s == null ? null : parseFloat(s);

export function adaptDebt(d: ApiDebt): Debt {
  return {
    id: d.id,
    name: d.name,
    type: d.debt_type,
    calcCategory: d.calc_category,
    status: d.status,
    balanceSource: d.balance_source,
    currentBalance: num(d.current_balance),
    interestRate: num(d.apr),
    minimumPayment: num(d.minimum_payment),
    escrowAmount: num(d.escrow_amount),
    dueDate: d.due_date,
    nextDueDate: d.next_due_date,
    paymentFrequency: d.payment_frequency,
    accountId: d.account_id,
    lastFour: d.last_four,
    extraPaymentsAllowed: d.extra_payments_allowed ?? true,
    lender: d.lender ?? "",
    notes: d.notes ?? "",
    originalAmount: d.original_amount == null ? null : num(d.original_amount),
    termMonths: d.term_months,
  };
}

function adaptBrief(b: ApiStrategyBrief): StrategyBrief {
  return {
    payoffDate: b.payoff_date,
    months: b.months,
    totalInterest: nullableNum(b.total_interest),
    neverPaysOff: b.never_pays_off,
  };
}

function adaptRecommendationBranch(
  b: ApiStrategyRecommendationBranch,
): StrategyRecommendationBranch {
  return {
    firstDebtName: b.first_debt_name,
    firstDebtMonths: b.first_debt_months,
    firstDebtPayoffDate: b.first_debt_payoff_date,
    totalInterest: num(b.total_interest),
    monthsToDebtFree: b.months_to_debt_free,
    neverPaysOff: b.never_pays_off,
  };
}

function adaptRecommendation(r: ApiStrategyRecommendation): StrategyRecommendation {
  return {
    recommended: r.recommended,
    reason: r.reason,
    noPreference: r.no_preference ?? false,
    snowball: adaptRecommendationBranch(r.snowball),
    avalanche: adaptRecommendationBranch(r.avalanche),
  };
}

function adaptSummary(s: ApiDebtSummary): DebtSummary {
  return {
    totalDebt: num(s.total_debt),
    accountCount: s.account_count,
    payoffDateMinimums: s.payoff_date_minimums,
    payoffDateStrategy: s.payoff_date_strategy,
    monthsSaved: s.months_saved,
    timeSavedLabel: s.time_saved_label,
    interestSaved: nullableNum(s.interest_saved),
    interestTotalMinimums: nullableNum(s.interest_total_minimums),
    interestTotalStrategy: nullableNum(s.interest_total_strategy),
    minimumsNeverPayOff: s.minimums_never_pay_off ?? false,
    strategies: {
      snowball: adaptBrief(s.strategies.snowball),
      avalanche: adaptBrief(s.strategies.avalanche),
    },
    recommendation: adaptRecommendation(s.recommendation),
    neverPaysOff: s.never_pays_off,
    dueDateWarning: s.due_date_warning,
    projectionExcludedCount: s.projection_excluded_count ?? 0,
    projectionUnavailableMessage: s.projection_unavailable_message ?? null,
  };
}

function adaptPlan(p: ApiDebtPlan): DebtPlan {
  return {
    strategy: p.strategy,
    extraPayment: num(p.extra_payment),
    extraPaymentSource: p.extra_payment_source,
    payFrequency: p.pay_frequency,
    payFrequencyLabel: p.pay_frequency_label,
    extraPaymentMonthly:
      p.extra_payment_monthly !== undefined ? num(p.extra_payment_monthly) : undefined,
    extraPaymentApplies: p.extra_payment_applies ?? true,
  };
}

export function toDebtOverview(api: ApiDebtOverview): DebtOverview {
  const perDebtPayoff: Record<string, DebtPayoffInfo> = {};
  for (const [id, p] of Object.entries(api.per_debt_payoff)) {
    perDebtPayoff[id] = {
      order: p.order,
      payoffDate: p.payoff_date,
      durationLabel: p.duration_label,
      neverPaysOff: p.never_pays_off,
      payoffDateMinimums: p.payoff_date_minimums,
      projectedBalanceAtFocus:
        p.projected_balance_at_focus == null ? null : num(p.projected_balance_at_focus),
      totalPayment: p.total_payment == null ? null : num(p.total_payment),
      minimumMonthly: p.minimum_monthly == null ? null : num(p.minimum_monthly),
      anchor: p.anchor,
      projectionUnavailable: p.projection_unavailable ?? false,
      nothingOwed: p.nothing_owed ?? false,
    };
  }
  return {
    debts: api.debts.map(adaptDebt),
    paidOffDebts: (api.paid_off_debts ?? []).map(adaptDebt),
    plan: adaptPlan(api.plan),
    summary: adaptSummary(api.summary),
    perDebtPayoff,
    balanceOverTime: api.balance_over_time.map((pt) => ({
      year: pt.year,
      minimums: num(pt.minimums),
      strategy: num(pt.strategy),
    })),
  };
}
